/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SiteDashboardShell, { useSiteDashboard } from "@/components/customer/SiteDashboardShell";
import { fetchSiteOrder, changeSiteOrderStatus, updateSiteOrderPayment, createSiteShipment } from "@/lib/api";

function money(minor) { return `₱${((minor || 0) / 100).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`; }

const ACTIONS_BY_STATUS = {
  new: [["start_processing", "Start Processing"], ["cancel", "Cancel"]],
  processing: [["mark_packed", "Mark Packed"], ["cancel", "Cancel"]],
  packed: [["ready_to_ship", "Ready to Ship"], ["cancel", "Cancel"]],
  ready_to_ship: [["cancel", "Cancel"]],
};

export default function SiteOrderDetail() {
  const { orderId } = useParams();
  const dashboard = useSiteDashboard();
  const { token, slug } = dashboard;
  const [order, setOrder] = useState(null);
  const [shipCarrier, setShipCarrier] = useState("LBC");
  const [shipTracking, setShipTracking] = useState("");

  const load = () => { if (token) fetchSiteOrder(token, slug, orderId).then(setOrder).catch(() => {}); };
  useEffect(load, [token, slug, orderId]);

  if (!order) return <SiteDashboardShell active="orders" dashboard={dashboard}><p className="text-sm text-[#475569]">Loading…</p></SiteDashboardShell>;

  return (
    <SiteDashboardShell active="orders" dashboard={dashboard}>
      <Link to={`/account/websites/${slug}/orders`} className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#10B981] mb-4"><ArrowLeft size={15} /> All orders</Link>
      <h2 className="text-lg font-bold font-display text-[#0F172A]">{order.order_number}</h2>
      <p className="text-sm text-[#475569]">{order.customer_name} · {order.customer_email || order.customer_phone || "No contact on file"}</p>

      <div className="mt-4 rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden">
        {order.items.map((item, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0] last:border-0 text-sm">
            <span>{item.product_name}{item.sku ? ` (${item.sku})` : ""} × {item.quantity}</span>
            <span className="font-medium">{money(item.line_total_minor)}</span>
          </div>
        ))}
        <div className="px-4 py-3 text-sm text-[#475569] space-y-0.5">
          <p>Subtotal: {money(order.subtotal_minor)}</p>
          {order.discount_minor > 0 && <p>Discount: -{money(order.discount_minor)}</p>}
          {order.shipping_amount_minor > 0 && <p>Shipping: {money(order.shipping_amount_minor)}</p>}
          {order.tax_amount_minor > 0 && <p>Tax: {money(order.tax_amount_minor)}</p>}
          <p className="font-bold text-[#0F172A]">Total: {money(order.total_minor)}</p>
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-4"><p className="text-xs text-[#94A3B8]">Payment</p><p className="mt-1 font-semibold capitalize">{order.payment_status}</p></div>
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-4"><p className="text-xs text-[#94A3B8]">Fulfillment</p><p className="mt-1 font-semibold capitalize">{order.fulfillment_status.replace(/_/g, " ")}</p></div>
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-4"><p className="text-xs text-[#94A3B8]">Shipping</p><p className="mt-1 font-semibold capitalize">{order.shipping_status.replace(/_/g, " ")}</p></div>
      </div>

      <div className="mt-4 flex gap-2 flex-wrap">
        {(ACTIONS_BY_STATUS[order.fulfillment_status] || []).map(([action, label]) => (
          <button key={action} onClick={() => changeSiteOrderStatus(token, slug, order.id, action).then(load)}
            className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-1.5 text-sm font-medium hover:border-[#10B981]">{label}</button>
        ))}
        {order.payment_status !== "paid" && (
          <button onClick={() => updateSiteOrderPayment(token, slug, order.id, { payment_status: "paid" }).then(load)}
            className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-1.5 text-sm font-medium hover:border-[#10B981]">Mark Paid</button>
        )}
      </div>

      {order.fulfillment_status === "ready_to_ship" && (
        <div className="mt-4 rounded-2xl border border-[#E2E8F0] bg-white p-5">
          <p className="text-sm font-semibold text-[#0F172A] mb-2">Create Shipment</p>
          <div className="flex gap-2 flex-wrap">
            <select value={shipCarrier} onChange={(e) => setShipCarrier(e.target.value)} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm bg-white">
              <option>LBC</option><option>J&T Express</option><option>Ninja Van</option><option>DHL</option><option>Other</option>
            </select>
            <input placeholder="Tracking number" value={shipTracking} onChange={(e) => setShipTracking(e.target.value)} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
            <button onClick={() => createSiteShipment(token, slug, order.id, { carrier: shipCarrier, tracking_number: shipTracking || undefined }).then(load)}
              className="rounded-lg bg-[#0F172A] px-4 py-2 text-sm font-medium text-white">Mark Shipped</button>
          </div>
        </div>
      )}

      {order.shipments?.length > 0 && (
        <div className="mt-4 rounded-2xl border border-[#E2E8F0] bg-white p-5">
          <p className="text-sm font-semibold text-[#0F172A] mb-2">Shipment</p>
          {order.shipments.map((s) => (
            <p key={s.id} className="text-sm text-[#475569]">{s.carrier} — {s.tracking_number || "no tracking number"} — <span className="capitalize">{s.status.replace(/_/g, " ")}</span>
              {s.tracking_url && <> · <a href={s.tracking_url} target="_blank" rel="noopener noreferrer" className="text-[#10B981] hover:underline">Track Shipment</a></>}
            </p>
          ))}
        </div>
      )}

      <div className="mt-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8] mb-2">Order history</p>
        <ul className="space-y-1 text-sm text-[#475569]">
          {order.history.map((h, i) => <li key={i}>{new Date(h.at).toLocaleString()} — {h.event}{h.note ? ` (${h.note})` : ""}</li>)}
        </ul>
      </div>
    </SiteDashboardShell>
  );
}
